/**
 * Pure validation helpers for the admin CRUD forms.
 *
 * Rules enforce role-based write permissions; the UI enforces shape + uniqueness.
 * Keep this file dependency-free (no React, no Firestore) so it stays easy to unit test.
 */

export interface ClientFormValues {
  name: string;
  website?: string;
}

export type FormErrors<T> = Partial<Record<keyof T, string>>;

export function validateClientForm(values: ClientFormValues): FormErrors<ClientFormValues> {
  const errors: FormErrors<ClientFormValues> = {};
  if (!values.name || values.name.trim() === '') {
    errors.name = 'Name is required';
  }
  if (values.website && values.website.trim() !== '') {
    try {
      // `new URL` throws on malformed URLs and rejects bare strings.
      const parsed = new URL(values.website);
      if (!/^https?:$/.test(parsed.protocol)) {
        errors.website = 'URL must use http or https';
      }
    } catch {
      errors.website = 'Invalid URL';
    }
  }
  return errors;
}

import { CATALOG_SECTIONS, BILLING_MODES, type CatalogSection, type BillingMode } from '@altyor-kronos/shared-types';

export interface CatalogDesignationFormValues {
  name: string;
  section: CatalogSection | '';
  variants: string[];
  billingMode: BillingMode;
}

export function validateCatalogDesignationForm(
  values: CatalogDesignationFormValues,
): FormErrors<CatalogDesignationFormValues> {
  const errors: FormErrors<CatalogDesignationFormValues> = {};
  if (!values.name || values.name.trim() === '') {
    errors.name = 'Name is required';
  }
  if (!values.section || !(CATALOG_SECTIONS as readonly string[]).includes(values.section)) {
    errors.section = 'Section is required';
  }
  if (!values.billingMode || !(BILLING_MODES as readonly string[]).includes(values.billingMode)) {
    errors.billingMode = 'Billing mode is required';
  }
  return errors;
}

// ─── HourlyRate (Phase 9) ──────────────────────────────────────────

export interface HourlyRateFormValues {
  label: string;
  /** Stored as a string from TextField — we parse + validate here. */
  amount: string;
}

export function validateHourlyRateForm(
  values: HourlyRateFormValues,
): FormErrors<HourlyRateFormValues> {
  const errors: FormErrors<HourlyRateFormValues> = {};
  if (!values.label || values.label.trim() === '') {
    errors.label = 'Label is required';
  }
  const amount = Number(values.amount);
  if (values.amount === '' || !Number.isFinite(amount) || amount < 0) {
    errors.amount = 'Amount must be a positive number';
  } else if (amount > 100000) {
    errors.amount = 'Amount is unreasonably large';
  }
  return errors;
}

/**
 * Case-insensitive trim-normalised uniqueness check for hourly-rate
 * labels. Finance users rely on the label to identify a rate in the
 * Finance tab dropdown; duplicates would be ambiguous.
 */
export function isHourlyRateLabelUnique(
  candidate: string,
  existing: readonly { id: string; label: string }[],
  ignoreId?: string,
): boolean {
  const normalised = candidate.trim().toLowerCase();
  if (normalised === '') return true; // handled by the required check
  return !existing.some(
    (r) => r.id !== ignoreId && r.label.trim().toLowerCase() === normalised,
  );
}

